import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import Navbar from '../components/Navbar'
import BottomNav from '../components/BottomNav'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'

const C = {
  textPrimary:   'var(--text)',
  textSecondary: 'var(--text-muted)',
  textTertiary:  'var(--text-faint)',
  textSuccess:   'var(--green)',
  textDanger:    'var(--red)',
  bg:            'var(--bg-surface)',
  border:        'var(--border)',
  borderFaint:   'var(--border-faint)',
}

const TYPE_LABELS = {
  purchase:     { label: 'Purchase',      icon: '💳', color: C.textSuccess },
  tip_sent:     { label: 'Tip sent',      icon: '🎁', color: C.textDanger },
  tip_received: { label: 'Tip received',  icon: '🪙', color: C.textSuccess },
  cashout:      { label: 'Cashout',        icon: '💸', color: C.textDanger },
  refund:       { label: 'Refund',         icon: '↩️', color: C.textSuccess },
}

const PAGE_SIZE = 20

export default function TransactionHistoryPage() {
  const { user } = useAuth()
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)

  const fetchPage = useCallback(async (offset = 0) => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return []
    const { data } = await axios.get('/api/coins/history', {
      params: { limit: PAGE_SIZE, offset },
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
    return data
  }, [])

  useEffect(() => {
    fetchPage(0)
      .then(data => {
        setTransactions(data)
        setHasMore(data.length === PAGE_SIZE)
      })
      .catch(err => console.error('[TransactionHistoryPage] Failed to fetch first page:', err))
      .finally(() => setLoading(false))
  }, [fetchPage])

  async function loadMore() {
    setLoadingMore(true)
    try {
      const data = await fetchPage(transactions.length)
      setTransactions(prev => [...prev, ...data])
      setHasMore(data.length === PAGE_SIZE)
    } catch {}
    setLoadingMore(false)
  }

  function formatDate(dateStr) {
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  function formatTime(dateStr) {
    const d = new Date(dateStr)
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  }

  return (
    <>
      <Navbar />
      <main id="main-content" style={{ maxWidth: 600, margin: '0 auto', padding: '24px 16px 100px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.textPrimary, margin: 0 }}>Transaction history</h1>
          <Link to="/settings?tab=Payments" style={{ fontSize: 13, color: 'var(--purple)', textDecoration: 'none' }}>
            Back to Payments
          </Link>
        </div>

        {loading ? (
          <p style={{ fontSize: 14, color: C.textTertiary, textAlign: 'center', padding: 40 }}>Loading…</p>
        ) : transactions.length === 0 ? (
          <p style={{ fontSize: 14, color: C.textTertiary, textAlign: 'center', padding: 40 }}>No transactions yet.</p>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, borderRadius: 12, overflow: 'hidden', border: `1px solid ${C.border}` }}>
              {transactions.map(tx => {
                const info = TYPE_LABELS[tx.type] ?? { label: tx.type, icon: '•', color: C.textPrimary }
                const isPositive = tx.amount > 0
                return (
                  <div key={tx.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 16px', background: C.bg,
                    borderBottom: `1px solid ${C.borderFaint}`,
                  }}>
                    <span style={{ fontSize: 20, width: 32, textAlign: 'center', flexShrink: 0 }}>{info.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: C.textPrimary }}>{info.label}</div>
                      <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {tx.description}
                        {tx.counterparty && (
                          <> — <Link to={`/user/${tx.counterparty.username}`} style={{ color: 'var(--purple)', textDecoration: 'none' }}>@{tx.counterparty.username}</Link></>
                        )}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: isPositive ? C.textSuccess : C.textDanger }}>
                        {isPositive ? '+' : ''}{tx.amount.toLocaleString()}
                      </div>
                      <div style={{ fontSize: 11, color: C.textTertiary, marginTop: 2 }}>
                        {formatDate(tx.created_at)}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="btn btn-outline"
                  style={{ fontSize: '0.85rem', padding: '8px 24px' }}
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </main>
      <BottomNav />
    </>
  )
}
